import React, { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { usePromiseModal } from '@prezly/react-promise-modal';

import TimelineType from './Type';
import TimelineMeta from './Meta';
import TimelineAuthor from './Author';
import MemorySelectModal from '../MemorySelectModal';

import { setShareModalTimeline } from '../../helpers/timeline';

import type { Memory } from '../../types/memory';
import type { Timeline } from '../../types/timeline';

interface TimelineHeaderProps {
  timeline: Timeline;
  mode?: 'public' | 'private';
  handleAddMemory?: (memories: Memory[]) => void;
};

const TimelineHeader: React.FC<TimelineHeaderProps> = ({ timeline, mode = 'private', handleAddMemory }) => {
  const nav = useNavigate();

  const addMemoryDropdownListener = (event: CustomEvent) => {
    const action = event.detail.item.value;
    if (action === 'new') {
      nav({ to: `/timelines/${timeline.id}/create` });
    }
    if (action === 'existing') {
      if (handleAddMemory) {
        handleAddExistingMemory();
      }
    }
  };

  useEffect(() => {
    const initializeDropdown = async () => {
      await customElements.whenDefined('wa-button');
      const dropdown = document.getElementById(`timeline-header-dropdown-${timeline.id}`) as HTMLElement | null;
      if (dropdown) {
        dropdown.addEventListener('wa-select', addMemoryDropdownListener as EventListener);
      }
    };

    initializeDropdown();

    return () => {
      const dropdown = document.getElementById(`timeline-header-dropdown-${timeline.id}`) as HTMLElement | null;
      if (dropdown) {
        dropdown.removeEventListener('wa-select', addMemoryDropdownListener as EventListener);
      }
    }
  }, []);

  const selectMemory = usePromiseModal(
    ({ show, onSubmit, onDismiss }) => (
      <MemorySelectModal
        timelineId={timeline.id}
        show={show}
        onConfirm={(memories: Memory[]) => onSubmit(memories)}
        onDismiss={onDismiss}
      />
    )
  );

  const handleAddExistingMemory = async () => {
    const memories = await selectMemory.invoke() as Memory[];
    if (memories && memories.length > 0 && handleAddMemory) {
      handleAddMemory(memories);
    }
  };

  return (
    <div className="timeline-header-inner wa-grid">
      <div className="timeline-header-left wa-stack wa-gap-0">
        <TimelineType timeline={timeline} />
        <h1>{timeline.dedicated_to}</h1>
        <TimelineMeta timeline={timeline} />
        {mode === 'public' && (
          <TimelineAuthor timeline={timeline} />
        )}
      </div>
      {mode === 'private' && (
        <div className="timeline-header-right wa-cluster wa-gap-2">
          {handleAddMemory && (
            <wa-dropdown id={`timeline-header-dropdown-${timeline.id}`} size="s" placement="bottom-end">
              <wa-button size="s" slot="trigger">
                <i className="fa-light fa-plus" slot="start" />
                Add memory
              </wa-button>
              <wa-dropdown-item value="new">
                <i className="fa-light fa-plus" slot="icon" />
                Create new memory
              </wa-dropdown-item>
              <wa-dropdown-item value="existing">
                <i className="fa-light fa-table-layout" slot="icon" />
                Add existing memory
              </wa-dropdown-item>
            </wa-dropdown>
          )}
          <wa-button size="s" appearance="outlined" data-dialog="open share-modal" onClick={() => setShareModalTimeline(timeline.id)}>
            <i className="fa-light fa-arrow-up-from-bracket" slot="start" />
            Share
          </wa-button>
        </div>
      )}
      {selectMemory.modal}
    </div>
  );
}

export default TimelineHeader;
